import {
  formatRoute,
  ADMINISTRATION,
  MY_5_DYNAMICS,
  NETWORK,
  NEWS,
  SETUP_RELATIONSHIPS_ACCOUNT,
  TEAMS,
  TOKEN_ADMINISTRATION,
  TRAINING_CENTER,
} from 'constants/urls'
import { get } from 'lodash'
import { Link, useLocation } from 'react-router-dom'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import classnames from 'classnames'
import PropTypes from 'prop-types'
import React, { useLayoutEffect, useRef } from 'react'
import * as networkSelectors from 'app_modules/network/selectors'
import * as sessionSelectors from 'app_modules/session/selectors'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import * as notificationActions from 'app_modules/notifications/actions'
import * as proMemberActions from 'app_modules/proMember/actions'

import { ACCOUNT_ADMIN, MEMBER, TOKEN_BUYER } from 'roles'
import { I18N, PERSON, ACTIONS, NETWORK_DETAILS } from 'constants/props'
import Translation from 'generics/Translation'

import styles from './menu.scss'

// MUI Tabs sets tabIndex -1 on non-selected items (tablist pattern). Restore full tab order for route links.
// Re-run when `tabsValue` changes — MUI reapplies tabIndex on each Tabs value update; a bare [] would miss that.
const NavTabsTabOrderFix = ({ children, tabsValue }) => {
  const wrapRef = useRef(null)
  useLayoutEffect(() => {
    const root = wrapRef.current
    if (!root) return
    const tabs = root.querySelectorAll('[role="tab"]')
    for (let i = 0; i < tabs.length; i += 1) {
      tabs[i].tabIndex = 0
    }
    // tabsValue is listed in deps only to re-run after MUI updates tabIndex (mirrors <Tabs value={...} />); not read here.
  }, [tabsValue])

  return (
    <div ref={wrapRef} data-mui-tabs-value={tabsValue}>
      {children}
    </div>
  )
}

NavTabsTabOrderFix.propTypes = {
  children: PropTypes.node.isRequired,
  tabsValue: PropTypes.string.isRequired,
}

const MenuDesktop = ({ i18n, isStudentWaitingResults, profile, proMemberActionsProps, networkDetails }) => {
  const location = useLocation()

  if (profile === null) return null

  const { newsNotifications, account, limitedMember: limitedMemberFromProfile } = profile
  const { flags: learningMenuTabsVisibility, id: accountId, isPersonal: isPersonalAccount } = account
  const { limitedMember: limitedMemberFromNetworkDetails } = networkDetails || {}

  const isLimitedMember =
    limitedMemberFromNetworkDetails === undefined ? limitedMemberFromProfile : limitedMemberFromNetworkDetails

  const {
    administrationTab: isAdministrationTabVisible,
    networkTab: isNetworkTabVisible,
    newsTab: isNewsTabVisible,
    relationshipsTab: isRelationshipsTabVisible,
    teamsTab: isTeamsTabVisible,
    tokenAdministrationTab: isTokenAdministrationTabVisible,
    trainingCenterTab: isTrainingCenterTabVisible,
  } = learningMenuTabsVisibility || {}

  // Visibility for menu tabs validations for roles
  const isCorporateAdminRole = !isPersonalAccount && profile.hasRole(ACCOUNT_ADMIN)
  const isCorporateMemberRole = !isPersonalAccount && profile.hasRole(MEMBER)
  const isCorporateTokenBuyerRole = !isPersonalAccount && profile.hasRole(TOKEN_BUYER)
  const { isLicenseBuyer } = profile?.permissions?.find(({ isLicenseBuyer: licenseBuyer }) => licenseBuyer) || {}
  const showMySubscriptions = !isCorporateAdminRole && isLicenseBuyer

  const getMenu = () => [
    {
      id: 'my-5-dynamics',
      isVisible: true,
      isClickable: true,
      label: i18n.menu.my5Dynamics,
      path: formatRoute(MY_5_DYNAMICS, { accountId }),
    },
    {
      id: 'network',
      isVisible: isNetworkTabVisible,
      isClickable: true,
      label: i18n.menu.network,
      path: formatRoute(NETWORK, { accountId }),
    },
    {
      id: 'teams',
      isVisible: isTeamsTabVisible && !isStudentWaitingResults,
      isClickable: true,
      label: i18n.menu.teams,
      path: formatRoute(TEAMS, { accountId }),
    },
    {
      id: 'training-center',
      isVisible: isTrainingCenterTabVisible,
      isClickable: isCorporateMemberRole || !isLimitedMember || isLimitedMember,
      label: i18n.menu.trainingCenter,
      path: formatRoute(TRAINING_CENTER, { accountId }),
    },
    {
      id: 'administration',
      isVisible: (isAdministrationTabVisible && isCorporateAdminRole) || showMySubscriptions,
      isClickable: true,
      label: showMySubscriptions ? i18n.menu.mySubscriptions : i18n.menu.administration,
      path: formatRoute(ADMINISTRATION, { accountId, sectionId: 'people' }),
    },
    {
      hasNotification: newsNotifications,
      id: 'news',
      isVisible: isNewsTabVisible,
      isClickable: true,
      label: i18n.menu.news,
      path: formatRoute(NEWS, { accountId }),
    },
    {
      id: 'relationships',
      isVisible: isCorporateMemberRole && !isStudentWaitingResults && isRelationshipsTabVisible,
      isClickable: isCorporateMemberRole && !isStudentWaitingResults && isRelationshipsTabVisible,
      label: i18n.menu.relationships,
      path: formatRoute(SETUP_RELATIONSHIPS_ACCOUNT, { accountId }),
    },
    {
      id: 'token-administration',
      isVisible: isTokenAdministrationTabVisible && (isCorporateAdminRole || isCorporateTokenBuyerRole),
      isClickable: isTokenAdministrationTabVisible && (isCorporateAdminRole || isCorporateTokenBuyerRole),
      label: i18n.menu.tokenAdministration,
      path: formatRoute(TOKEN_ADMINISTRATION, { accountId, sectionId: 'people' }),
    },
  ]

  const showProMemberModal = (e, isClickable) => {
    if (!isClickable) {
      e.preventDefault()
      proMemberActionsProps.toggleProMemberModal(true)
    }
  }

  const renderMenuListItems = () =>
    getMenu()
      .filter(({ isVisible }) => isVisible)
      .map(({ hasNotification, isClickable, id, label, path }) => (
        <Tab
          component={Link}
          className={classnames(styles.link, {
            [styles.notification]: hasNotification,
            [styles.disabled]: !isClickable,
          })}
          data-test="header-nav-link"
          onClick={e => showProMemberModal(e, isClickable)}
          to={path}
          href={path}
          id={id}
          key={id}
          label={label}
          title={label}
          value={id}
        />
      ))

  // Derive active tab from pathname — matches the old match.params.tab logic.
  // Pathname shape: /account/:accountId/:tab?/... — tab is segment index 3.
  const pathSegments = location.pathname.split('/')
  const tab = get(pathSegments, '[3]', 'my-5-dynamics') || 'my-5-dynamics'

  return (
    <nav className={classnames(styles.menu, { [styles.large]: profile.hasRole(ACCOUNT_ADMIN) })}>
      <NavTabsTabOrderFix tabsValue={tab}>
        <Tabs
          classes={{
            flexContainer: styles.justifyCenter,
            indicator: styles['tab-indicator'],
          }}
          textColor="inherit"
          value={tab}
        >
          {renderMenuListItems()}
        </Tabs>
      </NavTabsTabOrderFix>
    </nav>
  )
}

MenuDesktop.propTypes = {
  i18n: I18N.isRequired,
  isStudentWaitingResults: PropTypes.bool.isRequired,
  profile: PERSON,
  networkDetails: NETWORK_DETAILS.isRequired,
  proMemberActionsProps: ACTIONS.isRequired,
}

MenuDesktop.defaultProps = {
  profile: null,
}

const mapStateToProps = state => {
  const { id } = sessionSelectors.currentProfile(state)

  return {
    networkDetails: networkSelectors.networkDetails.profileNetworkDetails(state, id),
  }
}

const mapDispatchToProps = dispatch => ({
  proMemberActionsProps: bindActionCreators(proMemberActions, dispatch),
  notificationActionsProps: bindActionCreators(notificationActions, dispatch),
})

export default connect(mapStateToProps, mapDispatchToProps)(Translation(MenuDesktop, ['menu']))
